import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../../../store';
import {
  searchAgent,
  setSelectedUser,
  setCurrentStep,
  setSelectedPublisherId,
  resetAgentState,
  sendOTP,
  verifyOTP,
  grantPublisher,
  getAgents,
} from '../../../../Slice/reseller/agent/agentSlice';
import { toast } from 'react-toastify';
import useIdentities from '../../../../hooks/useIdentities';

interface AddAgentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddAgentModal: React.FC<AddAgentModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    loading,
    searchResults,
    selectedUser,
    error,
    currentStep,
    existingAgents,
  } = useSelector((state: RootState) => state.agent);
  const { data: identities } = useIdentities();

  const [mobileInput, setMobileInput] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [publisherId, setPublisherId] = useState<number | null>(null);
  const [isExistingAgent, setIsExistingAgent] = useState(false);

  // Fetch existing agents when modal opens
  useEffect(() => {
    if (open) {
      dispatch(getAgents());
    } else {
      dispatch(resetAgentState());
      setMobileInput('');
      setOtpCode('');
      setPublisherId(null);
      setIsExistingAgent(false);
    }
  }, [open, dispatch]);

  // Search for agent when mobile input changes
  useEffect(() => {
    if (mobileInput.length >= 4) {
      const timer = setTimeout(() => {
        dispatch(searchAgent({ mobile: mobileInput }));
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timer);
    }
  }, [mobileInput, dispatch]);

  const handleUserSelect = (user: any, fromExisting: boolean = false) => {
    if (user) {
      dispatch(setSelectedUser(user));
      setIsExistingAgent(fromExisting);

      if (fromExisting) {
        // Skip OTP verification for existing agents, go directly to publisher selection
        dispatch(setCurrentStep('selectPublisher'));
      } else {
        // New agents need to go through confirmation and OTP
        dispatch(setCurrentStep('confirm'));
      }
    }
  };

  const handleExistingAgentSelect = (agent: any) => {
    handleUserSelect(agent, true);
  };

  const handleConfirm = async () => {
    if (selectedUser) {
      const result = await dispatch(
        sendOTP({ child_user_id: selectedUser.id }),
      );
      if (!result.payload?.error) {
        dispatch(setCurrentStep('verifyOTP'));
      }
    }
  };

  const handleVerifyOTP = async () => {
    if (selectedUser && otpCode) {
      await dispatch(
        verifyOTP({ child_user_id: selectedUser.id, code: otpCode }),
      );
      // If successful, will automatically go to selectPublisher step
    }
  };

  const handleGrantPublisher = async () => {
    if (selectedUser && publisherId) {
      const result = await dispatch(
        grantPublisher({
          child_user_id: selectedUser.id,
          publisher_id: publisherId,
        }),
      );
      if (!result.payload?.error) {
        // Find the selected identity to get its name
        const selectedIdentity = identities?.find(
          (id) => id.id === publisherId,
        );

        // Save representative to localStorage
        const storedRepresentatives = localStorage.getItem(
          'identity_representatives',
        );
        const representativesMap = storedRepresentatives
          ? JSON.parse(storedRepresentatives)
          : {};

        if (!representativesMap[publisherId]) {
          representativesMap[publisherId] = [];
        }

        // Check if representative already exists
        const existingIndex = representativesMap[publisherId].findIndex(
          (rep: any) => rep.mobile === selectedUser.mobile,
        );

        if (existingIndex === -1) {
          // Add new representative
          representativesMap[publisherId].push({
            name: selectedUser.name || '',
            mobile: selectedUser.mobile,
            id: selectedUser.id,
          });
          localStorage.setItem(
            'identity_representatives',
            JSON.stringify(representativesMap),
          );
        }

        // Show success toast
        toast.success(
          `نماینده با شماره ${selectedUser.mobile} به هویت ${selectedIdentity?.name || ''} اضافه شد`,
          {
            position: 'top-center',
            autoClose: 3000,
          },
        );

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }

        // Close modal
        onClose();
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 'confirm') {
      dispatch(setCurrentStep('search'));
      dispatch(setSelectedUser(null));
    } else if (currentStep === 'verifyOTP') {
      dispatch(setCurrentStep('confirm'));
    } else if (currentStep === 'selectPublisher') {
      // If coming from existing agent selection, go back to search
      // Otherwise, go back to OTP verification
      if (isExistingAgent) {
        dispatch(setCurrentStep('search'));
        dispatch(setSelectedUser(null));
        setIsExistingAgent(false);
      } else {
        dispatch(setCurrentStep('verifyOTP'));
      }
    }
  };

  const renderSearchStep = () => (
    <>
      <DialogContent sx={{ maxHeight: '500px', overflowY: 'auto' }}>
        <Box>
          {/* Section 1: Add new agent */}
          <Typography
            sx={{
              fontFamily: 'IRANYekanXFaNum',
              fontSize: '15px',
              fontWeight: 600,
              color: '#1F2937',
              mb: 2,
              pb: 1,
              borderBottom: '2px solid #E5E7EB',
            }}
          >
            افزودن نماینده جدید
          </Typography>

          <Typography
            sx={{
              fontFamily: 'IRANYekanXFaNum',
              fontSize: '13px',
              fontWeight: 400,
              color: '#6B7280',
              mb: 2,
            }}
          >
            شماره موبایل نماینده را وارد کنید:
          </Typography>

          <Autocomplete
            options={searchResults}
            getOptionLabel={(option) => option.mobile}
            loading={loading}
            value={selectedUser}
            onChange={(_, newValue) => handleUserSelect(newValue)}
            inputValue={mobileInput}
            onInputChange={(_, newInputValue) => setMobileInput(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="09xxxxxxxxx"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'IRANYekanXFaNum',
                    direction: 'ltr',
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  fontFamily: 'IRANYekanXFaNum',
                  direction: 'rtl',
                  padding: '10px 16px',
                  borderBottom: '1px solid #F3F4F6',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'IRANYekanXFaNum',
                        fontSize: '11px',
                        color: '#6B7280',
                      }}
                    >
                      موبایل:
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'IRANYekanXFaNum',
                        fontSize: '13px',
                        fontWeight: 500,
                        direction: 'ltr',
                      }}
                    >
                      {option.mobile}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'IRANYekanXFaNum',
                        fontSize: '11px',
                        color: '#6B7280',
                      }}
                    >
                      نام:
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'IRANYekanXFaNum',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: option.name ? '#1F2937' : '#9CA3AF',
                      }}
                    >
                      {option.name || 'ندارد'}
                    </Typography>
                  </Box>
                  {option.has_parent && (
                    <Typography
                      sx={{
                        fontFamily: 'IRANYekanXFaNum',
                        fontSize: '10px',
                        color: '#EF4444',
                        mt: 0.5,
                        textAlign: 'center',
                      }}
                    >
                      ⚠️ دارای نماینده اصلی
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            noOptionsText="نتیجه‌ای یافت نشد"
            loadingText="در حال جستجو..."
          />

          {error && (
            <Typography
              sx={{
                fontFamily: 'IRANYekanXFaNum',
                fontSize: '12px',
                color: '#EF4444',
                mt: 2,
              }}
            >
              {error}
            </Typography>
          )}

          {/* Section 2: Select from existing agents */}
          {existingAgents.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography
                sx={{
                  fontFamily: 'IRANYekanXFaNum',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#1F2937',
                  mb: 2,
                  pb: 1,
                  borderBottom: '2px solid #E5E7EB',
                }}
              >
                انتخاب از نمایندگان فعلی
              </Typography>

              <Typography
                sx={{
                  fontFamily: 'IRANYekanXFaNum',
                  fontSize: '13px',
                  fontWeight: 400,
                  color: '#6B7280',
                  mb: 2,
                }}
              >
                یکی از نمایندگان زیر را انتخاب کنید:
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {existingAgents.map((agent) => (
                  <Paper
                    key={agent.id}
                    onClick={() => handleExistingAgentSelect(agent)}
                    elevation={0}
                    sx={{
                      padding: '14px 16px',
                      borderRadius: '8px',
                      border: '1.5px solid #E5E7EB',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#F0F9FF',
                        borderColor: '#3B82F6',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'IRANYekanXFaNum',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#6B7280',
                        }}
                      >
                        موبایل:
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'IRANYekanXFaNum',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1F2937',
                          direction: 'ltr',
                        }}
                      >
                        {agent.mobile}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'IRANYekanXFaNum',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#6B7280',
                        }}
                      >
                        نام:
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'IRANYekanXFaNum',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: agent.name ? '#1F2937' : '#9CA3AF',
                        }}
                      >
                        {agent.name || 'ندارد'}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={onClose}
          sx={{
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '14px',
            color: '#6B7280',
          }}
        >
          انصراف
        </Button>
      </DialogActions>
    </>
  );

  const renderConfirmStep = () => (
    <>
      <DialogContent>
        <Box
          sx={{
            minHeight: 220,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'IRANYekanXFaNum',
              fontSize: '13px',
              fontWeight: 500,
              color: '#25282E',
              mb: 3,
              textAlign: 'center',
            }}
          >
            اطلاعات کاربر:
          </Typography>

          {selectedUser && (
            <Paper
              elevation={2}
              sx={{
                padding: '18px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #BAE6FD 0%, #7DD3FC 100%)',
                border: '1px solid #E5E5E8',
                width: '80%',
                margin: '0 auto',
                minHeight: '130px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'IRANYekanXFaNum',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#0C4A6E',
                  }}
                >
                  موبایل:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'IRANYekanXFaNum',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#075985',
                    direction: 'ltr',
                  }}
                >
                  {selectedUser.mobile}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'IRANYekanXFaNum',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#0C4A6E',
                  }}
                >
                  نام:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'IRANYekanXFaNum',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: selectedUser.name ? '#075985' : '#9CA3AF',
                  }}
                >
                  {selectedUser.name || 'ندارد'}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'IRANYekanXFaNum',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#0C4A6E',
                  }}
                >
                  شناسه:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'IRANYekanXFaNum',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#075985',
                  }}
                >
                  {selectedUser.id}
                </Typography>
              </Box>
            </Paper>
          )}

          {error && (
            <Typography
              sx={{
                fontFamily: 'IRANYekanXFaNum',
                fontSize: '12px',
                color: '#EF4444',
                mt: 2,
              }}
            >
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
        <Button
          onClick={handleBack}
          sx={{
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '14px',
            color: '#6B7280',
          }}
        >
          بازگشت
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
          sx={{
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '14px',
            backgroundColor: '#3B82F6',
            '&:hover': {
              backgroundColor: '#2563EB',
            },
          }}
        >
          {loading ? <CircularProgress size={20} /> : 'ارسال کد تایید'}
        </Button>
      </DialogActions>
    </>
  );

  const renderVerifyOTPStep = () => (
    <>
      <DialogContent>
        <Box sx={{ minHeight: 160 }}>
          <Typography
            sx={{
              fontFamily: 'IRANYekanXFaNum',
              fontSize: '13px',
              fontWeight: 500,
              color: '#25282E',
              mb: 2,
            }}
          >
            کد تایید ارسال شده را وارد کنید:
          </Typography>

          <TextField
            fullWidth
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
            placeholder="کد تایید"
            variant="outlined"
            inputProps={{
              maxLength: 6,
              style: {
                fontFamily: 'IRANYekanXFaNum',
                direction: 'ltr',
                textAlign: 'center',
                fontSize: '18px',
                letterSpacing: '8px',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'IRANYekanXFaNum',
              },
            }}
          />

          {error && (
            <Typography
              sx={{
                fontFamily: 'IRANYekanXFaNum',
                fontSize: '12px',
                color: '#EF4444',
                mt: 2,
              }}
            >
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
        <Button
          onClick={handleBack}
          sx={{
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '14px',
            color: '#6B7280',
          }}
        >
          بازگشت
        </Button>
        <Button
          onClick={handleVerifyOTP}
          variant="contained"
          disabled={loading || otpCode.length < 4}
          sx={{
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '14px',
            backgroundColor: '#10B981',
            '&:hover': {
              backgroundColor: '#059669',
            },
          }}
        >
          {loading ? <CircularProgress size={20} /> : 'تایید نهایی'}
        </Button>
      </DialogActions>
    </>
  );

  const renderSelectPublisherStep = () => (
    <>
      <DialogContent>
        <Box
          sx={{
            minHeight: 220,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'IRANYekanXFaNum',
              fontSize: '13px',
              fontWeight: 500,
              color: '#25282E',
              mb: 3,
              textAlign: 'center',
            }}
          >
            هویت مورد نظر را انتخاب کنید:
          </Typography>

          <FormControl fullWidth>
            <InputLabel
              sx={{
                fontFamily: 'IRANYekanXFaNum',
                fontSize: '14px',
                '&.Mui-focused': {
                  color: '#3B82F6',
                },
              }}
            >
              انتخاب هویت
            </InputLabel>
            <Select
              value={publisherId || ''}
              onChange={(e) => setPublisherId(Number(e.target.value))}
              label="انتخاب هویت"
              sx={{
                fontFamily: 'IRANYekanXFaNum',
                '& .MuiSelect-select': {
                  fontFamily: 'IRANYekanXFaNum',
                },
              }}
            >
              {identities?.map((identity) => (
                <MenuItem
                  key={identity.id}
                  value={identity.id}
                  sx={{
                    fontFamily: 'IRANYekanXFaNum',
                    fontSize: '14px',
                    direction: 'rtl',
                  }}
                >
                  {identity.name} ({identity.display_name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {error && (
            <Typography
              sx={{
                fontFamily: 'IRANYekanXFaNum',
                fontSize: '12px',
                color: '#EF4444',
                mt: 2,
                textAlign: 'center',
              }}
            >
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
        <Button
          onClick={handleBack}
          sx={{
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '14px',
            color: '#6B7280',
          }}
        >
          بازگشت
        </Button>
        <Button
          onClick={handleGrantPublisher}
          variant="contained"
          disabled={loading || !publisherId}
          sx={{
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '14px',
            backgroundColor: '#10B981',
            '&:hover': {
              backgroundColor: '#059669',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'تایید و افزودن'
          )}
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          direction: 'rtl',
          maxWidth: '420px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: 'IRANYekanXFaNum',
          fontSize: '18px',
          fontWeight: 600,
          color: '#1F2937',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        افزودن نماینده
      </DialogTitle>

      {currentStep === 'search' && renderSearchStep()}
      {currentStep === 'confirm' && renderConfirmStep()}
      {currentStep === 'verifyOTP' && renderVerifyOTPStep()}
      {currentStep === 'selectPublisher' && renderSelectPublisherStep()}
    </Dialog>
  );
};

export default AddAgentModal;
